import { inject } from '@angular/core';
import {
  CanMatchFn,
  Route,
  Router,
  UrlSegment,
  UrlTree,
} from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { Observable, filter, map, of, race, take, timeout } from 'rxjs';

import { selectUser } from '@/app/dashboard/store/dashboard.selectors';
import {
  loadUser,
  loadUserFailure,
} from '@/app/dashboard/store/dashboard.actions';
import { Store } from '@ngrx/store';
import { ToastService } from '@/app/shared/components/toast/services/toast.service';

import { selectCurrentUserPermissions } from './permissions.selectors';
import { PermissionId } from './permission.types';

/**
 * Route gate that allows navigation only when the current user holds the
 * permission(s) declared on the route via `data`.
 *
 * Usage in `app.routes.ts`:
 * ```ts
 * {
 *   path: 'create-transaction',
 *   canMatch: [authenticatedGuard, permissionGuard],
 *   data: { permission: 'transaction.create' },
 *   loadComponent: () => import('./…').then(m => m.CreateTransactionComponent),
 * }
 * ```
 *
 * `data.permission` may be a single `PermissionId` or an array. When an
 * array is supplied, `data.permissionMode` (`'any'` | `'all'`, default
 * `'any'`) controls the match semantics — same as the directive.
 *
 * Denial behaviour: redirects to `/dashboard` and surfaces a localized
 * toast (`permissions.notAllowedToast`). Crucially, denial **does not log
 * the user out** — lack of permission is not lack of authentication, and
 * the auth interceptor's 401 carve-out must not be triggered here.
 *
 * The guard waits for the dashboard user profile to load before deciding,
 * because the profile is fetched once on shell boot and an evaluation
 * against an empty profile would deny every route on a hard refresh. The
 * wait ends after `PROFILE_WAIT_MS`: a profile that never settles (a hung
 * request, a failure dispatched before this guard subscribed, a profile with
 * no role) cancels the navigation with a "request failed" toast instead of
 * leaving the router pending forever (F-023).
 */
export const PROFILE_WAIT_MS = 10_000;

export const permissionGuard: CanMatchFn = (
  route: Route,
  _segments: UrlSegment[]
): Observable<boolean | UrlTree> => {
  const store = inject(Store);
  const actions$ = inject(Actions);
  const router = inject(Router);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  const required = normalize(route.data?.['permission']);
  const mode = route.data?.['permissionMode'] === 'all' ? 'all' : 'any';

  if (required.length === 0) {
    // No declared permission — guard is a no-op so the route falls through
    // to the next guard / matches.
    return store.select(selectCurrentUserPermissions).pipe(
      take(1),
      map(() => true as const)
    );
  }

  // Belt-and-suspenders: `authenticatedGuard` also dispatches, but keep this
  // so permission-gated routes still boot the profile if reached otherwise.
  store.dispatch(loadUser());

  return race(
    store.select(selectUser).pipe(
      filter(user => !!user?.role),
      take(1)
    ),
    actions$.pipe(ofType(loadUserFailure), take(1))
  ).pipe(
    take(1),
    timeout({
      first: PROFILE_WAIT_MS,
      with: () => {
        toast.error(translate.instant('common.errors.requestFailed'));
        return of(false as const);
      },
    }),
    map(result => {
      if (result === false) {
        return false;
      }
      if (!result || !('role' in result) || !result.role) {
        return router.createUrlTree(['/dashboard']);
      }

      let allowed: ReadonlySet<string> = new Set();
      store
        .select(selectCurrentUserPermissions)
        .subscribe(set => (allowed = set))
        .unsubscribe();

      const ok =
        mode === 'all'
          ? required.every(id => allowed.has(id))
          : required.some(id => allowed.has(id));

      if (ok) {
        return true as const;
      }

      toast.warning(translate.instant('permissions.notAllowedToast'));
      return router.createUrlTree(['/dashboard']);
    })
  );
};

function normalize(value: unknown): readonly PermissionId[] {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string');
  }
  if (typeof value === 'string') {
    return [value];
  }
  return [];
}
